// packages/api/src/PublicRsvpService.ts
//
// PUBLIC RSVP client — the read (`publicRsvpForm`) that returns everything the
// website needs to render the RSVP form, and the write (`publicRsvp`) that submits
// it. Anonymous + published-only, mirroring the eventsmagic-api PublicRsvpCore.
//
// Both run through the injected client (see `configureApiService`) so the same
// service works server-side and, crucially here, in the BROWSER — the RSVP form is
// an interactive client component that reads on open and writes on submit.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { ANSWER_IMAGE_FIELDS, QUESTION_IMAGE_FIELDS, SIGNUP_PAID_FIELD, executeMutationImageSafe, executeQueryImageSafe, imageSafeDocs } from "./answerImageFields";
import type { AnswerAddressInput } from "./addressAnswer";
import { refusedNewInputs } from "./refusedNewInputs";
import { errorMessages } from "./opResultHelpers";
import { PUBLIC_CAUSE_FIELD_LIST, type PublicCause } from "./PublicOrganizerService";

// ── Read (form) shapes ──────────────────────────────────────────────────────

/**
 * What the waitlist claim page renders (ROADMAP #36). `found` / `claimed` / `expired` are STATES,
 * not errors — a guest opening the link two days late must be told the offer ran out, not shown a
 * dead page.
 */
export interface PublicWaitlistOffer {
  found: boolean;
  /** They already hold a place (claimed, or the host let them in by hand). */
  claimed?: boolean;
  /** The claim window closed and the place went to the next person in the queue. */
  expired?: boolean;
  eventId?: string | null;
  eventSlug?: string | null;
  eventName?: string | null;
  eventWhen?: string | null;
  guestName?: string | null;
  /** How many places are held (the party's size). */
  partySize?: number | null;
  /** When the hold lapses (ISO). */
  expiresAt?: string | null;
  /** A ticketed event needs a card to complete the claim. */
  isTicketed?: boolean | null;
  rsvpStatus?: string | null;
  /** ROADMAP #36 ticketed rail: the ticket type held (null = the event as a whole). */
  ticketTypeId?: string | null;
  /** That type's name, so the page says "your VIP place" rather than "your place". */
  ticketTypeName?: string | null;
  /**
   * ROADMAP #36 ticketed rail: where a ticketed claimant completes their purchase. The checkout
   * carries the same token, so the held place is given back to them instead of counting as taken.
   * Null on the RSVP rail, on a claimed or expired offer, or when no public origin is configured.
   */
  checkoutUrl?: string | null;
}

export interface PublicRsvpEventRef {
  id: string;
  title?: string | null;
  slug?: string | null;
}

export interface PublicRsvpFormOption {
  id: string;
  label?: string | null;
  value?: string | null;
  price?: number | null;
}

export interface PublicRsvpFormQuestion {
  id: string;
  /** single_value | single_choice | multi_choice | file | address (A2e #86 — a whole postal address; valueType unused) */
  questionType?: string | null;
  /** short_text | long_text | phone | email | url | integer | decimal | amount | age | date | time | datetime | boolean | rating */
  valueType?: string | null;
  title?: string | null;
  helpText?: string | null;
  placeholder?: string | null;
  isRequired?: boolean | null;
  /**
   * WHO must answer: none | all | primary | adults.
   *
   * ⚠ Use this, not `isRequired`, to decide whether to block THIS guest. `isRequired` only says
   * somebody must answer. Marking a child's email as required is what makes a parent type their own
   * address into it — and the server matches people by email alone, so the child then resolves to
   * the parent's contact.
   */
  requiredScope?: string | null;
  /** The person attribute this answer maps to (email / phone / birthday / …; `address` = the whole address) — used to
   *  find the birthdate question that drives age-gated policies. */
  personField?: string | null;
  /**
   * A2e #91 — a picture question's shape: `avatar` (square, at least 512 × 512) | `logo` (square or wider, up to 2:1).
   * Null for every other type, and from a gateway that predates it (the site then treats a picture question as a photo).
   */
  imageShape?: string | null;
  /** Asked once for the whole party (on the primary guest) vs once per guest. */
  askOncePerGroup?: boolean | null;
  /** How this question's visibility conditions combine: 'all' | 'any'. */
  conditionMatch?: string | null;
  /** Visibility conditions — show only for certain responses / groups. Empty = always shown (to attendees). */
  conditions?: PublicRsvpFormQuestionCondition[] | null;
  orderNo?: number | null;
  options?: PublicRsvpFormOption[] | null;
}

export interface PublicRsvpFormQuestionCondition {
  /** rsvp_status | guest_group | answer */
  conditionKind?: string | null;
  /** in | not_in | equals | not_equals */
  operator?: string | null;
  /** rsvp_status values matched (for conditionKind = rsvp_status). */
  rsvpStatuses?: (string | null)[] | null;
  /** The guest group matched (for conditionKind = guest_group). */
  guestGroupId?: string | null;
}

export interface PublicRsvpFormGroup {
  id: string;
  name?: string | null;
  color?: string | null;
  /** Whether joining this group needs a password. */
  hasPassword?: boolean | null;
}

export interface PublicRsvpFormGroupSet {
  id: string;
  name?: string | null;
  color?: string | null;
  icon?: string | null;
  /** Whether a guest may create a new group in this set (e.g. a new team name). */
  canCreate?: boolean | null;
  groups?: PublicRsvpFormGroup[] | null;
}

export interface PublicRsvpFormSignupItem {
  id: string;
  name?: string | null;
  description?: string | null;
  quantityAvailable?: number | null;
  maxQuantityPerGuest?: number | null;
  /** How many are still available (null = unlimited). */
  remaining?: number | null;
  price?: number | null;
  priceUnit?: string | null;
  currencyCode?: string | null;
}

/** One legal policy / waiver attached to the event, resolved for the RSVP form. */
export interface PublicRsvpFormPolicy {
  /** The event_policy id — what an acceptance points at. */
  id: string;
  /** The underlying account policy id. */
  policyId?: string | null;
  name?: string | null;
  /** Short "I agree to …" label (falls back to the name). */
  acceptanceLabel?: string | null;
  description?: string | null;
  /** The waiver text (empty ⇒ a simple checkbox consent). */
  body?: string | null;
  hasBody?: boolean | null;
  /** plain | markdown | html | richtext. */
  contentFormat?: string | null;
  isRequired?: boolean | null;
  /** Age band the policy applies to (null = everyone). */
  minAge?: number | null;
  maxAge?: number | null;
  ageGated?: boolean | null;
  requireInitials?: boolean | null;
  requireSignature?: boolean | null;
  /** A self-sign policy — the guest must sign it themselves (adult can't be signed for). */
  selfSign?: boolean | null;
}

export interface PublicRsvpForm {
  event?: PublicRsvpEventRef | null;
  /** The RSVP is for members only: the form asks for a membership number (one use per guest who takes a place). */
  /** #289 — the causes this form offers a gift to (empty = it asks for none). */
  giftCauses?: PublicCause[] | null;
  /** …their gifts take TEST cards. */
  giftsTestMode?: boolean | null;
  membersOnly?: boolean | null;
  /** The membership types it accepts, by name (empty = any membership). */
  membershipTypeNames?: string[] | null;
  /** Whether the event is accepting RSVPs right now. When false, show `message`. */
  accepting?: boolean | null;
  message?: string | null;
  /** yes_maybe_no | yes_no | submit */
  rsvpButtonSet?: string | null;
  /** names | headcount */
  guestTracking?: string | null;
  rsvpGroupSize?: number | null;
  guestsNeedApproval?: boolean | null;
  /**
   * ROADMAP #36 — the event is FULL but the host runs a waitlist, so the form stays OPEN and a
   * submission is recorded as `waitlisted`. `message` then carries an informational notice to show
   * ABOVE the fields, not instead of them.
   */
  waitlisting?: boolean | null;
  /** Remaining capacity (null = unlimited). Display only. */
  spotsLeft?: number | null;
  questions?: PublicRsvpFormQuestion[] | null;
  /** Guest-group sets offered on the form (empty = no group selection). */
  groupSets?: PublicRsvpFormGroupSet[] | null;
  /** Whether a guest may set a password on a group they create. */
  allowGroupPassword?: boolean | null;
  /** Whether guests may sign up for signup-list items during RSVP. */
  signupsEnabled?: boolean | null;
  /** Signup-list items offered on the form. */
  signupItems?: PublicRsvpFormSignupItem[] | null;
  /** Legal policies / waivers the guest must accept (empty = none). */
  policies?: PublicRsvpFormPolicy[] | null;
  /**
   * The age at which a guest signs a waiver themselves rather than a parent or guardian signing for
   * them — `event.ageOfMajority`, default 18. Comes from the server BECAUSE it is not universally
   * 18: it varies within countries (19 in BC, 16 in Scotland, 21 in Mississippi) and moves over
   * time, so the host sets it per event and the form must not assume.
   */
  ageOfMajority?: number | null;
  /** single | recurring | agenda — how the event is scheduled. */
  scheduleMode?: string | null;
  /** Whether this event offers RSVP-able time slots (recurring occurrences / agenda sessions). */
  timeSlotsEnabled?: boolean | null;
  /** Whether picking ≥1 slot is REQUIRED (recurring) vs optional (agenda sessions). */
  timeSlotsRequired?: boolean | null;
  /** The RSVP-able slots the guest picks from (empty when not applicable). */
  timeSlots?: PublicRsvpFormTimeSlot[] | null;
  /** Whether to show each guest their check-in QR code (guest_code) on the confirmation + email. */
  qrEnabled?: boolean | null;
  /** Base URL for the "Add to Apple Wallet" link (<url>/wallet/apple?code=<guest_code>); null when not configured. */
  walletAppleUrl?: string | null;
  /** Base URL for the "Add to Google Wallet" link (<url>/wallet/google?code=<guest_code>); null when not configured. */
  walletGoogleUrl?: string | null;
}

/** One RSVP-able time slot (a recurring occurrence or an agenda sub-event). */
export interface PublicRsvpFormTimeSlot {
  id: string;
  name?: string | null;
  description?: string | null;
  /** UTC start/end (render in the event timezone). */
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  track?: string | null;
  capacity?: number | null;
  /** Spots left (null = unlimited). */
  remaining?: number | null;
  rsvpDeadline?: string | null;
  deadlinePassed?: boolean | null;
  /** ROADMAP A4: the slot has already taken place — hidden unless the guest picked it. */
  ended?: boolean | null;
  onlineMeetingUrl?: string | null;
}

// ── Write (submit) shapes ───────────────────────────────────────────────────

export interface PublicRsvpAnswerInput {
  questionId: string;
  valueText?: string | null;
  valueNumber?: number | null;
  valueBoolean?: boolean | null;
  /** ISO date/datetime string. */
  valueDate?: string | null;
  /** Selected option ids for single_choice / multi_choice. */
  optionIds?: string[] | null;
  /** A2e #86 — an address question's answer (`toAddressInput`). */
  valueAddress?: AnswerAddressInput | null;
  /** A2e #91 — a picture question's answer: the file and token `PublicRsvpService.uploadAnswerImage` returned. */
  valueImage?: AnswerImageInput | null;
}

/** A2e #91 — a picture already uploaded for one question; the token proves it was uploaded for THAT question. */
export interface AnswerImageInput {
  uploadedFileId: string;
  token: string;
}

/** A2e #91 — a stored or just-uploaded picture answer, with its URL to show. */
export interface AnswerImageValue extends AnswerImageInput {
  uploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
}

export interface PublicRsvpGuestGroupInput {
  setId: string;
  /** Pick an existing group (mutually exclusive with newGroupName). */
  groupId?: string | null;
  /** Create/join a group by name (only for creatable sets). */
  newGroupName?: string | null;
  /** Required to join a protected group; optional to protect a group you create. */
  password?: string | null;
}

export interface PublicRsvpGuestInput {
  /** When editing (remember-me), the ref of the prior guest to update (from getMine). Omit for a new guest. */
  ref?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** This guest's attendance decision: yes | maybe | no. Allowed set comes from rsvpButtonSet; defaults to yes. */
  decision?: string | null;
  /** Headcount this record covers (headcount tracking: a lead guest + party size). Defaults to 1. */
  partySize?: number | null;
  answers?: PublicRsvpAnswerInput[] | null;
  /** This guest's group picks (at most one per set). */
  groups?: PublicRsvpGuestGroupInput[] | null;
  /** This guest's signup-list picks. Present array (even empty) replaces prior; omitted leaves them. */
  signups?: PublicRsvpGuestSignupInput[] | null;
  /** The time slots (recurring occurrences / agenda sessions) this guest will attend. Present array (even empty) replaces prior; omitted leaves them. */
  timeSlotIds?: string[] | null;
  /** This guest's age — collected when the event has an age-gated policy. */
  age?: number | null;
  /** This guest's policy acceptances (checkbox/acknowledge consents). */
  policyAcceptances?: PublicRsvpPolicyAcceptanceInput[] | null;
  /** The adult who signs this guest's waivers when they are a minor. */
  guardian?: PublicRsvpGuardianInput | null;
}

export interface PublicRsvpPolicyAcceptanceInput {
  eventPolicyId: string;
  accepted?: boolean | null;
}

/**
 * The adult who signs a MINOR's waivers. One per guest, not one per policy.
 *
 * ⚠ A minor's waiver is NOT signed on the registration form. Whoever is filling it in is
 * unverified, so a signature captured there is a name somebody typed and a squiggle they drew. We
 * record WHO the guardian is and the server emails THEM a link to sign on their own device.
 */
export interface PublicRsvpGuardianInput {
  name?: string | null;
  /** At least one of email / phone — it is where the signing link goes. */
  email?: string | null;
  phone?: string | null;
  relationship?: string | null;
}


/** A tokenized link a guest must use to sign a self-sign waiver themselves. */
export interface PublicRsvpSignLink {
  policyName?: string | null;
  guestName?: string | null;
  url?: string | null;
}

export interface PublicRsvpGuestSignupInput {
  itemId: string;
  quantity?: number | null;
  note?: string | null;
}

export interface PublicRsvpInput {
  /** The event-website URL (host + path) the form was opened on. */
  url: string;
  eventTimeSlotId?: string | null;
  /** Remember-me id from the visitor cookie (null on first submit → server mints one). */
  visitorId?: string | null;
  /** Host invite token (from a personal `?inv=` link) — binds this submission to the invited guest(s). */
  inviteToken?: string | null;
  /** The membership number — required when the RSVP is for members only. */
  membershipNumber?: string | null;
  guests: PublicRsvpGuestInput[];
  /** An optional note the guest leaves for the host (party-level). */
  messageToHost?: string | null;
  /** Consent toggles (party-level). Omitted defaults to true server-side. */
  optInForMessages?: boolean | null;
  optInForReminders?: boolean | null;
  optInForRsvpConfirmation?: boolean | null;
  /**
   * A2b #59 — the `?ch=` marker that FIRST introduced this browser to the event (first-touch
   * attribution, decided in the browser by `lib/attribution.ts`). Null when the browser never
   * arrived through a tagged link; the report shows that as "Direct / untagged". A marker that
   * matches no channel is simply not credited.
   */
  marketingChannelCode?: string | null;
}

export interface PublicRsvpGuestResult {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  rsvpStatus?: string | null;
  /** The guest's code (guest_code). ⚠ Not a promise of a QR — see checkInCode. */
  ref?: string | null;
  /** #136 — the code to show as their check-in QR, or null (QR off, a ticketed event, or no place held yet). The server's rule. */
  checkInCode?: string | null;
}

export interface PublicRsvpOutcome {
  /** Echoed/minted visitor id — store it in a first-party cookie. */
  visitorId?: string | null;
  event?: PublicRsvpEventRef | null;
  guests?: PublicRsvpGuestResult[] | null;
  /** Sign-yourself links for adult self-sign waivers (surface / log them; delivery is future). */
  signLinks?: PublicRsvpSignLink[] | null;
  /** The party's durable friendly recovery code — save it to manage this RSVP from any device. */
  recoveryCode?: string | null;
}

// ── Self-sign waivers (the tokenized signing page) ───────────────────────────

/** One waiver a guest signs on the signing page (its own token). */
export interface PublicPolicyWaiver {
  token?: string | null;
  policyName?: string | null;
  body?: string | null;
  contentFormat?: string | null;
  requireSignature?: boolean | null;
  requireInitials?: boolean | null;
  signed?: boolean | null;
  /** Audit trail for a signed waiver (when + from where). */
  signedAt?: string | null;
  signedIp?: string | null;
  signedUserAgent?: string | null;
  /** ⚠ The signer — for a minor's waiver this is the PARENT / GUARDIAN, not the guest it is about. */
  signerName?: string | null;
  /** self | guardian. */
  acceptedByRole?: string | null;
}

export interface PublicPolicyToken {
  found?: boolean | null;
  /**
   * ⚠ Whose waivers these are — which for a MINOR is NOT the person holding the link. Their parent
   * or guardian opened it and is signing on their behalf, so the page must say "for <child>" rather
   * than address the reader as the guest.
   */
  guestName?: string | null;
  eventName?: string | null;
  /** A2e #101 — the name the page speaks as (its title): the brand, or the organizer on a white-label plan. */
  brandName?: string | null;
  /** The reader is a parent or guardian signing for the minor named in `guestName`. */
  forMinor?: boolean | null;
  /** The guardian this link was addressed to, when known. */
  signerName?: string | null;
  /** Resolved {{variable}} → value map for the waiver bodies (event/venue/host). */
  templateValues?: Record<string, string> | null;
  /** Every self-sign waiver this guest must sign (each with its own token). */
  waivers?: PublicPolicyWaiver[] | null;
}

export interface PublicPolicySignInput {
  token: string;
  /** Required when the policy requires a signature or initials; an Accept-only policy sends none (#164). */
  legalName?: string | null;
  signatureData?: string | null;
  initialsData?: string | null;
}

// ── "My RSVP" (remember-me / edit) shapes ────────────────────────────────────

export interface PublicRsvpMineAnswer {
  questionId: string;
  valueText?: string | null;
  valueNumber?: number | null;
  valueBoolean?: boolean | null;
  valueDate?: string | null;
  optionIds?: string[] | null;
  /** A2e #86 — an address question's stored answer. Withheld (masked) from a browser that has not proven who it is. */
  valueAddress?: AnswerAddressInput | null;
  /** A2e #91 — a picture question's stored answer, with a fresh token so sending it back unchanged passes. */
  valueImage?: AnswerImageValue | null;
}

export interface PublicRsvpMineGroup {
  setId?: string | null;
  groupId?: string | null;
  /** Whether this guest created the group (may change its password). */
  owned?: boolean | null;
}

export interface PublicRsvpMineGuest {
  /** Opaque ref for editing this exact guest — pass back as PublicRsvpGuestInput.ref. */
  ref?: string | null;
  /** #136 — the code to show as their check-in QR, or null (QR off, a ticketed event, or no place held yet). The server's rule. */
  checkInCode?: string | null;
  /**
   * #62 P6c — on an invite link whose identity is not proven: the email / phone questions this guest has a value on file
   * for, MASKED ("j•••@gmail.com"). Left blank, the server keeps the value on file.
   */
  maskedAnswers?: Array<{ questionId?: string | null; masked?: string | null }> | null;
  firstName?: string | null;
  lastName?: string | null;
  /** yes | maybe | no */
  decision?: string | null;
  /** The host reviewed this guest and did not confirm their place — render them read-only. */
  isDenied?: boolean | null;
  /** The host's reason, present ONLY when they chose to share it. Never an internal crew note. */
  deniedReason?: string | null;
  partySize?: number | null;
  answers?: PublicRsvpMineAnswer[] | null;
  /** the guest's current group picks (per set) — for prefilling the form. */
  groups?: PublicRsvpMineGroup[] | null;
  /** the guest's current signup-list picks — for prefilling the form. */
  signups?: PublicRsvpMineSignup[] | null;
  /** the time slots (occurrences / sessions) this guest picked — for prefilling the slot picker. */
  timeSlotIds?: string[] | null;
  /** the guest's current poll/quiz answers — for prefilling the poll block. */
  pollAnswers?: PublicRsvpMinePollAnswer[] | null;
  /** the party's self-reported travel segments — for prefilling the travel block (on the primary guest). */
  travelSegments?: PublicRsvpMineTravelSegment[] | null;
  /** the guest's budget pledges — for prefilling the budget block. */
  budgetPledges?: PublicRsvpMineBudgetPledge[] | null;
  /** the guest's prior policy acceptances — for prefilling the consent checkboxes. */
  policyAcceptances?: PublicRsvpMinePolicyAcceptance[] | null;
  /**
   * #69 — the guest removed every photo of themselves from this event's site. Null when the entry is not a guest yet
   * (nothing to remove). Selected by both `publicRsvpMine` and the invite read (a restore prefills from the latter).
   */
  photoHidden?: boolean | null;
  /** #69 — the site has some picture of them at all (a photo, or a Gravatar from their email). */
  hasPhoto?: boolean | null;
  /** #69 — the picture itself, only to a browser that has proven who it is. */
  photoUploadedFile?: { fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  photoGravatarUrl?: string | null;
}

/** #69 — remove every photo of a party guest from the event site, or show it again. */
export interface PublicRsvpGuestPhotoInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  /** The guest's `ref` from publicRsvpMine. */
  guestRef: string;
  hidden: boolean;
}

export interface PublicRsvpMinePolicyAcceptance {
  eventPolicyId?: string | null;
  accepted?: boolean | null;
}

export interface PublicRsvpMinePollAnswer {
  pollQuestionId?: string | null;
  selectedOptionIds?: string[] | null;
  answerText?: string | null;
}

/** An endpoint (from/to) on a guest's travel segment — a snapshot, however it was picked. */
export interface PublicTravelEndpoint {
  hubId?: string | null;
  venueId?: string | null;
  label?: string | null;
  code?: string | null;
  timezone?: string | null;
  detail?: string | null;
}

export interface PublicRsvpMineTravelSegment {
  id?: string | null;
  /** The party guests on this leg (their public codes). */
  guestRefs?: (string | null)[] | null;
  /** arrival | departure | transfer | other — the focus of the leg. */
  direction?: string | null;
  /** flight | train | bus | shuttle | car | ferry | rideshare | other */
  mode?: string | null;
  carrier?: string | null;
  /** Flight / train number. */
  serviceNumber?: string | null;
  bookingRef?: string | null;
  from?: PublicTravelEndpoint | null;
  to?: PublicTravelEndpoint | null;
  /** Departure instant (UTC ISO). */
  departsAt?: string | null;
  /** Arrival instant (UTC ISO). */
  arrivesAt?: string | null;
  note?: string | null;
}

export interface PublicRsvpMineBudgetPledge {
  /** The budget item pledged toward; null for a new (off-list) item. */
  budgetItemId?: string | null;
  /** New-item name (off-list pledges). */
  name?: string | null;
  categoryId?: string | null;
  amount?: number | null;
  /** 'pledge' (intent) or 'paid' (guest says they already paid the host). */
  kind?: string | null;
  /** True once the host has confirmed this as a real payment — no longer editable by the guest. */
  confirmed?: boolean | null;
  note?: string | null;
}

export interface PublicRsvpMineSignup {
  /** signup_item | poi | accommodation | travel */
  targetKind?: string | null;
  /** The target entity id (signup item / POI / …). */
  targetId?: string | null;
  /** Alias of targetId for signup_item sign-ups (null for other kinds). */
  itemId?: string | null;
  quantity?: number | null;
  note?: string | null;
  /** Period start (accommodation check-in, YYYY-MM-DD); null when not applicable. */
  startsOn?: string | null;
  /** Period end (accommodation check-out, YYYY-MM-DD); null when not applicable. */
  endsOn?: string | null;
  /** #149 — paid for online (a sign-up order): shown as paid, never offered for change. Absent on an older gateway. */
  paid?: boolean | null;
}

export interface PublicRsvpMine {
  event?: PublicRsvpEventRef | null;
  /** Whether this visitor has an RSVP for the event (false → fresh/forgotten browser). */
  found?: boolean | null;
  guests?: PublicRsvpMineGuest[] | null;
  /** #246 — the sign-up list items this party added: the ones it may remove. */
  addedSignupItemIds?: string[] | null;
  /** The party's RSVP-group label (e.g. a pasted "Damons Family") when the guest belongs to a named group. */
  rsvpGroupName?: string | null;
  /** #62 P6c — invite read: this link's guest can be sent a code to see their stored details ("Fill in my details"). */
  canVerify?: boolean | null;
  /** #62 — how many people this party may reply for (the host's per-guest cap, else the event's). */
  rsvpGroupSize?: number | null;
  messageToHost?: string | null;
  optInForMessages?: boolean | null;
  optInForReminders?: boolean | null;
  optInForRsvpConfirmation?: boolean | null;
  /** True when this prefill came from a host invite token (publicRsvpInvite), not the visitor cookie. */
  invited?: boolean | null;
  /** True when the invited guest has already submitted an RSVP (drives "you already responded" UI). */
  responded?: boolean | null;
  /** True when an already-answered invite link was opened on a different device: submitted data is
   *  withheld and the guest must enter a code (OTP / word-code) to view or change it. */
  requiresCode?: boolean | null;
  /** Whether to show each guest their check-in QR code (guest_code) — the event enabled guest QR. */
  qrEnabled?: boolean | null;
}

// ── The visitor's own coupons (show a venue rep) ──────────────────────────────

export interface PublicCouponItem {
  name?: string | null;
  quantity?: number | null;
}

export interface PublicCoupon {
  id?: string | null;
  /** The scannable code — rendered as a QR and shown as text. */
  code?: string | null;
  couponTypeId?: string | null;
  /** Voucher name, e.g. "Free Drink". */
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  /** Flat value per redemption (when there are no priced items). */
  couponValue?: number | null;
  /** What this coupon gives. */
  items?: PublicCouponItem[] | null;
  /** "issued" | "redeemed". */
  couponStatus?: string | null;
  redeemedCount?: number | null;
  /** How many times ONE coupon may be redeemed. */
  maxRedemptions?: number | null;
  /** An optional printed label (e.g. "Volunteer"). */
  issuedToLabel?: string | null;
  /** Which guest of the party holds it (multi-person RSVP); null for a group perk. */
  guestName?: string | null;
  /** ROADMAP A4: the date this coupon may be redeemed on (null = any date). */
  eventTimeSlotId?: string | null;
  /** That date as the guest reads it, in the event's timezone. */
  dateLabel?: string | null;
  /** That date's start (ISO-8601 UTC). */
  dateStartsAt?: string | null;
  /** Where to redeem it — the voucher type's place, else the event's (an entrance / desk); null when none is named. */
  redeemAt?: string | null;
}

export interface PublicCouponsMine {
  event?: PublicRsvpEventRef | null;
  /** Whether this visitor has an RSVP for the event (false → fresh/forgotten browser). */
  found?: boolean | null;
  coupons?: PublicCoupon[] | null;
}

// ── Inline signup (from the website's signup block) ───────────────────────────

export interface PublicSignupLineInput {
  /** A party guest's ref (guestCode from getMine). */
  guestRef: string;
  /** How many (signup items). Omit for participation kinds like POI. */
  quantity?: number | null;
  /** Optional note, e.g. "Greek salad" or "meet at 10am". A guest may have several lines of one target. */
  note?: string | null;
  /** Period start (accommodation check-in, YYYY-MM-DD). */
  startsOn?: string | null;
  /** Period end (accommodation check-out, YYYY-MM-DD). */
  endsOn?: string | null;
}

export interface PublicSignupAddItemInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  guestRef: string;
  name: string;
  /** How many they will bring. Absent = they only suggest it. */
  quantity?: number;
  note?: string;
  categoryId?: string;
}

export interface PublicSignupRemoveItemInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  itemId: string;
}

export interface PublicSignupInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  /** Which kind of target: signup_item | poi. Defaults to signup_item. */
  targetKind?: string | null;
  /** The target being signed up for (signup item / POI). Falls back to itemId. */
  targetId?: string | null;
  /** Back-compat alias of targetId for signup-list items. */
  itemId?: string | null;
  /** The lines to REPLACE this party's sign-ups for the target with. */
  lines: PublicSignupLineInput[];
}

export interface PublicSignupPartyGuest {
  ref?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface PublicSignupResultData {
  targetKind?: string | null;
  targetId?: string | null;
  /** Alias of targetId for signup-list items (null for other kinds). */
  itemId?: string | null;
  /** Remaining after the write (null = unlimited / no cap). */
  remaining?: number | null;
  party?: PublicSignupPartyGuest[] | null;
}

// ── Poll / quiz answering (from the event website's poll block) ────────────────

export interface PublicPollAnswerLineInput {
  /** A party guest's ref (guestCode from getMine). */
  guestRef: string;
  /** Chosen option ids (single = one, multi = many). Empty + no text clears the answer. */
  selectedOptionIds?: string[] | null;
  /** Free-text answer (free_text questions). */
  answerText?: string | null;
}

export interface PublicPollAnswerInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  pollQuestionId: string;
  /** Per-guest answers to this question (REPLACES each guest's prior answer). */
  lines: PublicPollAnswerLineInput[];
}

export interface PublicPollAnswerResultData {
  pollQuestionId?: string | null;
  answeredCount?: number | null;
}

export interface PublicPollOptionTally {
  optionId?: string | null;
  count?: number | null;
}

export interface PublicPollQuestionResults {
  pollQuestionId?: string | null;
  total?: number | null;
  options?: PublicPollOptionTally[] | null;
  freeTextCount?: number | null;
  /** Correct option ids — only present for a quiz the visitor has already answered, where a poll block reveals them. */
  correctOptionIds?: string[] | null;
  /** #147 — the counts are shown (a poll block shows results: right away, or once this party answered). */
  shown?: boolean | null;
  /** #147 — the options a party member added and may still rename. */
  editableOptions?: PublicPollEditableOption[] | null;
}

/** #147 — an option a party member added, which they may still rename (nobody else has chosen it). */
export interface PublicPollEditableOption {
  optionId?: string | null;
  guestRef?: string | null;
}

/** #147 — a guest adds their own option (it becomes their answer), or renames it (`optionId`). */
export interface PublicPollOptionInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  pollQuestionId: string;
  guestRef: string;
  label: string;
  /** Rename only — the option the guest added. */
  optionId?: string;
}

// ── Travel self-report (from the event website's travel block) ─────────────────

/** One endpoint (from/to) as the guest picked it: a hub, a Google Places result, or free text. */
export interface PublicTravelEndpointInput {
  /** A picked hub from the public catalog (airport/station). Its code/timezone are snapshotted server-side. */
  hubId?: string | null;
  /** Free-text / Google Places name of the place. */
  label?: string | null;
  code?: string | null;
  /** IANA timezone for this end (from the hub, or the guest's pick). */
  timezone?: string | null;
  /** Terminal / platform / meeting point. */
  detail?: string | null;
  /** Google Places lat/lng — used server-side to match an existing venue (stored as venueId). */
  lat?: number | null;
  lng?: number | null;
  placeId?: string | null;
}

export interface PublicTravelSegmentInput {
  /** Which party guests are on this leg (their public codes). Empty = the whole party. */
  guestRefs?: string[] | null;
  /** arrival | departure | transfer | other — the FOCUS of the leg. */
  direction?: string | null;
  /** flight | train | bus | shuttle | car | ferry | rideshare | other. */
  mode?: string | null;
  carrier?: string | null;
  /** Flight / train number. */
  serviceNumber?: string | null;
  bookingRef?: string | null;
  from?: PublicTravelEndpointInput | null;
  to?: PublicTravelEndpointInput | null;
  /** Departure instant (UTC ISO). At least one of departsAt/arrivesAt is required. */
  departsAt?: string | null;
  /** Arrival instant (UTC ISO). */
  arrivesAt?: string | null;
  note?: string | null;
}

export interface PublicTravelInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  /** The segments to REPLACE this party's guest-reported travel with. */
  segments: PublicTravelSegmentInput[];
}

export interface PublicTravelResultData {
  savedCount?: number | null;
  party?: PublicSignupPartyGuest[] | null;
}

/** A travel hub (airport/station) from the public catalog, for the from/to picker. */
export interface PublicTravelHub {
  id?: string | null;
  name?: string | null;
  code?: string | null;
  timezone?: string | null;
  kind?: string | null;
  locality?: string | null;
  regionCode?: string | null;
  countryCode?: string | null;
}

// ── Budget pledges (from the event website's budget block) ─────────────────────

export interface PublicBudgetContributionInput {
  /** Which party guest this contribution is from (their public code). Omit = the primary guest. */
  guestRef?: string | null;
  /** The budget item pledged toward. Omit for a new item (then newItemName is required). */
  budgetItemId?: string | null;
  /** Name for a new (off-list) item the guest will cover. */
  newItemName?: string | null;
  /** Category id for the new item (must be a budget category). */
  categoryId?: string | null;
  /** The amount (must be positive). */
  amount: number;
  /** 'pledge' (intent) or 'paid' (guest already paid the host). Defaults to 'pledge'. */
  contributionKind?: string | null;
  note?: string | null;
}

export interface PublicBudgetInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  /** The contributions to REPLACE this guest's prior pledges with. */
  contributions: PublicBudgetContributionInput[];
}

export interface PublicBudgetResultData {
  pledgedCount?: number | null;
  pledgedTotal?: number | null;
  paidCount?: number | null;
  paidTotal?: number | null;
  party?: PublicSignupPartyGuest[] | null;
}

// ── Upvotes (a household "like" on a signup item / POI / accommodation) ─────────

export interface PublicUpvoteInput {
  url: string;
  visitorId: string;
  eventTimeSlotId?: string | null;
  /** signup_item | poi | accommodation. */
  targetKind: string;
  targetId: string;
  /** Force the state (true = ensure on, false = ensure off). Omit to toggle. */
  upvoted?: boolean | null;
}

export interface PublicUpvoteResultData {
  targetKind?: string | null;
  targetId?: string | null;
  /** The party's NEW upvoted state for the target. */
  upvoted?: boolean | null;
  /** The target's NEW total upvote count. */
  count?: number | null;
}

export interface PublicUpvoteMine {
  targetKind?: string | null;
  targetId?: string | null;
}

export interface PublicUpvotesMineResultData {
  found?: boolean | null;
  upvotes?: PublicUpvoteMine[] | null;
}

// ── Operations ──────────────────────────────────────────────────────────────

const PUBLIC_RSVP_FORM_QUERY = imageSafeDocs(`
  query PublicRsvpForm($params: PublicRsvpFormInput) {
    publicRsvpForm(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        event {
          id
          title
          slug
        }
        accepting
        message
        giftCauses { ${PUBLIC_CAUSE_FIELD_LIST} }
        giftsTestMode
        membersOnly
        membershipTypeNames
        rsvpButtonSet
        guestTracking
        rsvpGroupSize
        guestsNeedApproval
        waitlisting
        spotsLeft
        questions {
          id
          questionType
          valueType
          title
          helpText
          placeholder
          isRequired
          requiredScope
          personField
          ${QUESTION_IMAGE_FIELDS}
          askOncePerGroup
          conditionMatch
          conditions {
            conditionKind
            operator
            rsvpStatuses
            guestGroupId
          }
          orderNo
          options {
            id
            label
            value
            price
          }
        }
        groupSets {
          id
          name
          color
          icon
          canCreate
          groups {
            id
            name
            color
            hasPassword
          }
        }
        allowGroupPassword
        signupsEnabled
        signupItems {
          id
          name
          description
          quantityAvailable
          maxQuantityPerGuest
          remaining
          price
          priceUnit
          currencyCode
        }
        policies {
          id
          policyId
          name
          acceptanceLabel
          description
          body
          hasBody
          contentFormat
          isRequired
          minAge
          maxAge
          ageGated
          requireInitials
          requireSignature
          selfSign
        }
        ageOfMajority
        scheduleMode
        timeSlotsEnabled
        timeSlotsRequired
        timeSlots {
          id
          name
          description
          startsAt
          endsAt
          isAllDay
          track
          capacity
          remaining
          rsvpDeadline
          deadlinePassed
          ended
          onlineMeetingUrl
        }
        qrEnabled
        walletAppleUrl
        walletGoogleUrl
      }
    }
  }
`);

// ⚠ Image-safe for `checkInCode` (#136): a gateway older than the field refuses the reply, and this is how a guest RSVPs.
const PUBLIC_RSVP_MUTATION = imageSafeDocs(`
  mutation PublicRsvp($params: PublicRsvpInput) {
    publicRsvp(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        visitorId
        event {
          id
          title
          slug
        }
        guests {
          firstName
          lastName
          email
          phone
          rsvpStatus
          ref
          checkInCode
        }
        signLinks {
          policyName
          guestName
          url
        }
        recoveryCode
      }
    }
  }
`);

// ── Cross-device RSVP restore ──
// #62 P6c — an invited guest asks for a code to THEIR contact on file ("Fill in my details").
const PUBLIC_RSVP_INVITE_REQUEST_CODE_MUTATION = gql`
  mutation PublicRsvpInviteRequestCode($params: PublicRsvpInviteCodeInput) {
    publicRsvpInviteRequestCode(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        sent
        sentTo
      }
    }
  }
`;

const PUBLIC_RSVP_REQUEST_CODE_MUTATION = gql`
  mutation PublicRsvpRequestCode($url: String!, $contact: String!) {
    publicRsvpRequestCode(url: $url, contact: $contact) {
      code
      errors {
        name
        errors
      }
      data {
        sent
      }
    }
  }
`;

const PUBLIC_RSVP_TRANSFER_CODE_MUTATION = gql`
  mutation PublicRsvpTransferCode($url: String!, $visitorId: String!) {
    publicRsvpTransferCode(url: $url, visitorId: $visitorId) {
      code
      errors {
        name
        errors
      }
      data {
        code
      }
    }
  }
`;

const PUBLIC_RSVP_GUEST_PHOTO_MUTATION = gql`
  mutation PublicRsvpGuestPhoto($params: PublicRsvpGuestPhotoInput) {
    publicRsvpGuestPhoto(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        guestRef
        photoHidden
      }
    }
  }
`;

// A2e #91 — upload the picture that answers a picture question (any public form).
const PUBLIC_ANSWER_IMAGE_UPLOAD_MUTATION = gql`
  mutation PublicAnswerImageUpload($params: PublicAnswerImageUploadInput) {
    publicAnswerImageUpload(params: $params) {
      code
      errors { name errors warnings }
      data { uploadedFileId token uploadedFile { id fileUrl thumbnailUrl } }
    }
  }
`;

const PUBLIC_RSVP_MY_CODE_MUTATION = gql`
  mutation PublicRsvpMyCode($url: String!, $visitorId: String!) {
    publicRsvpMyCode(url: $url, visitorId: $visitorId) {
      code
      errors {
        name
        errors
      }
      data {
        code
      }
    }
  }
`;

const PUBLIC_RSVP_REDEEM_CODE_MUTATION = gql`
  mutation PublicRsvpRedeemCode($url: String!, $code: String!) {
    publicRsvpRedeemCode(url: $url, code: $code) {
      code
      errors {
        name
        errors
      }
      data {
        inviteToken
        visitorId
      }
    }
  }
`;

const publicPolicyTokenQuery = (withBrand: boolean) => gql`
  query PublicPolicyToken($token: String!) {
    publicPolicyToken(token: $token) {
      code
      errors {
        name
        errors
      }
      data {
        found
        guestName
        eventName
        ${withBrand ? 'brandName' : ''}
        forMinor
        signerName
        templateValues
        waivers {
          token
          policyName
          body
          contentFormat
          requireSignature
          requireInitials
          signed
          signedAt
          signedIp
          signedUserAgent
          signerName
          acceptedByRole
        }
      }
    }
  }
`;

// A2e #101 — `brandName` (the page's title) is NEW on a whole page's query: a gateway not yet recomposed would refuse
// the WHOLE read and the waiver could not be signed. So the page asks with it and, only when the gateway does not know
// the field, again without — the page signs, just under the default title.
const PUBLIC_POLICY_TOKEN_QUERY = publicPolicyTokenQuery(true);
const PUBLIC_POLICY_TOKEN_QUERY_BASE = publicPolicyTokenQuery(false);

/** The gateway refused the waiver read because it does not know `brandName` yet (not recomposed) — nothing else. */
export const refusedUnknownPolicyBrand = (result: OpResult): boolean =>
  !result.didSucceed() && /Cannot query field ["']brandName["'] on type ["']PublicPolicyToken["']/.test(errorMessages(result).join("\n"));

/**
 * The waitlist claim page (ROADMAP #36). A guest promoted off a full event's waitlist gets an
 * emailed link carrying a signed token; these three back that page. No `url` — the token already
 * names the event, so there is nothing else for the link to carry (or get wrong).
 */
const PUBLIC_WAITLIST_OFFER_QUERY = gql`
  query PublicWaitlistOffer($token: String!) {
    publicWaitlistOffer(token: $token) {
      code
      errors {
        name
        errors
      }
      data {
        found
        claimed
        expired
        eventId
        eventSlug
        eventName
        eventWhen
        guestName
        partySize
        expiresAt
        isTicketed
        rsvpStatus
        ticketTypeId
        ticketTypeName
        checkoutUrl
      }
    }
  }
`;

const PUBLIC_WAITLIST_CLAIM_MUTATION = gql`
  mutation PublicWaitlistClaim($token: String!) {
    publicWaitlistClaim(token: $token) {
      code
      errors {
        name
        errors
      }
      data {
        claimed
        alreadyIn
        rsvpStatus
      }
    }
  }
`;

const PUBLIC_WAITLIST_DECLINE_MUTATION = gql`
  mutation PublicWaitlistDecline($token: String!) {
    publicWaitlistDecline(token: $token) {
      code
      errors {
        name
        errors
      }
      data {
        declined
      }
    }
  }
`;

const PUBLIC_POLICY_SIGN_MUTATION = gql`
  mutation PublicPolicySign($params: PublicPolicySignInput) {
    publicPolicySign(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        signed
        policyName
      }
    }
  }
`;

const PUBLIC_RSVP_MINE_QUERY = imageSafeDocs(`
  query PublicRsvpMine($params: PublicRsvpMineInput) {
    publicRsvpMine(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        event {
          id
          title
          slug
        }
        found
        addedSignupItemIds
        rsvpGroupName
        rsvpGroupSize
        messageToHost
        optInForMessages
        optInForReminders
        optInForRsvpConfirmation
        qrEnabled
        guests {
          ref
          checkInCode
          firstName
          lastName
          decision
          isDenied
          deniedReason
          partySize
          # #62 P6c — a browser bound by a plain submit has NOT proven who it is: its stored contact comes masked.
          maskedAnswers {
            questionId
            masked
          }
          answers {
            questionId
            valueText
            valueNumber
            valueBoolean
            valueDate
            optionIds
            valueAddress {
              streetAddress
              extraAddress
              city
              stateProvince
              postalCode
              country
            }
            ${ANSWER_IMAGE_FIELDS}
          }
          groups {
            setId
            groupId
            owned
          }
          signups {
            targetKind
            targetId
            itemId
            quantity
            note
            startsOn
            endsOn
            ${SIGNUP_PAID_FIELD}
          }
          timeSlotIds
          pollAnswers {
            pollQuestionId
            selectedOptionIds
            answerText
          }
          travelSegments {
            id
            guestRefs
            direction
            mode
            carrier
            serviceNumber
            bookingRef
            from { hubId venueId label code timezone detail }
            to { hubId venueId label code timezone detail }
            departsAt
            arrivesAt
            note
          }
          budgetPledges {
            budgetItemId
            name
            categoryId
            amount
            kind
            confirmed
            note
          }
          policyAcceptances {
            eventPolicyId
            accepted
          }
          photoHidden
          hasPhoto
          photoUploadedFile { fileUrl thumbnailUrl }
          photoGravatarUrl
        }
      }
    }
  }
`);

// Prefill from a host invite token — same PublicRsvpMine shape as remember-me (+ invited).
const PUBLIC_RSVP_INVITE_QUERY = imageSafeDocs(`
  query PublicRsvpInvite($params: PublicRsvpInviteInput) {
    publicRsvpInvite(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        event {
          id
          title
          slug
        }
        found
        invited
        responded
        requiresCode
        canVerify
        addedSignupItemIds
        rsvpGroupName
        rsvpGroupSize
        messageToHost
        optInForMessages
        optInForReminders
        optInForRsvpConfirmation
        qrEnabled
        guests {
          ref
          checkInCode
          firstName
          lastName
          decision
          isDenied
          deniedReason
          partySize
          # #62 P6c — contact on file, MASKED, on an invite link whose identity is not proven yet.
          maskedAnswers {
            questionId
            masked
          }
          answers {
            questionId
            valueText
            valueNumber
            valueBoolean
            valueDate
            optionIds
            valueAddress {
              streetAddress
              extraAddress
              city
              stateProvince
              postalCode
              country
            }
            ${ANSWER_IMAGE_FIELDS}
          }
          groups {
            setId
            groupId
            owned
          }
          signups {
            targetKind
            targetId
            itemId
            quantity
            note
            startsOn
            endsOn
            ${SIGNUP_PAID_FIELD}
          }
          timeSlotIds
          pollAnswers {
            pollQuestionId
            selectedOptionIds
            answerText
          }
          travelSegments {
            id
            guestRefs
            direction
            mode
            carrier
            serviceNumber
            bookingRef
            from { hubId venueId label code timezone detail }
            to { hubId venueId label code timezone detail }
            departsAt
            arrivesAt
            note
          }
          budgetPledges {
            budgetItemId
            name
            categoryId
            amount
            kind
            confirmed
            note
          }
          policyAcceptances {
            eventPolicyId
            accepted
          }
          photoHidden
          hasPhoto
          photoUploadedFile { fileUrl thumbnailUrl }
          photoGravatarUrl
        }
      }
    }
  }
`);

const PUBLIC_SIGNUP_ADD_ITEM_MUTATION = gql`
  mutation PublicSignupAddItem($params: PublicSignupAddItemInput) {
    publicSignupAddItem(params: $params) {
      code
      data { itemId name }
      errors { name errors warnings }
    }
  }
`;

const PUBLIC_SIGNUP_REMOVE_ITEM_MUTATION = gql`
  mutation PublicSignupRemoveItem($params: PublicSignupRemoveItemInput) {
    publicSignupRemoveItem(params: $params) {
      code
      data { itemId name }
      errors { name errors warnings }
    }
  }
`;

const PUBLIC_SIGNUP_MUTATION = gql`
  mutation PublicSignup($params: PublicSignupInput) {
    publicSignup(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        targetKind
        targetId
        itemId
        remaining
        party {
          ref
          firstName
          lastName
        }
      }
    }
  }
`;

const PUBLIC_POLL_ANSWER_MUTATION = gql`
  mutation PublicPollAnswer($params: PublicPollAnswerInput) {
    publicPollAnswer(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        pollQuestionId
        answeredCount
      }
    }
  }
`;

/** #147's result fields — newer than some gateways; a gateway that refuses them is asked again without (`refusedPollBlockFields`). */
const POLL_BLOCK_RESULT_FIELDS = `
        shown
        editableOptions {
          optionId
          guestRef
        }`;

const publicPollResultsQuery = (withBlockFields: boolean) => gql`
  query PublicPollResults($params: PublicPollResultsInput) {
    publicPollResults(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        pollQuestionId
        total
        freeTextCount
        correctOptionIds
        options {
          optionId
          count
        }${withBlockFields ? POLL_BLOCK_RESULT_FIELDS : ""}
      }
    }
  }
`;

const PUBLIC_POLL_RESULTS_QUERY = publicPollResultsQuery(true);
const PUBLIC_POLL_RESULTS_QUERY_BASE = publicPollResultsQuery(false);

/** #147 — the gateway refused the results read because it does not know `shown` / `editableOptions` yet — nothing else. */
export const refusedPollBlockFields = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["'](shown|editableOptions)["'] on type ["']PublicPollQuestionResults["']/.test(errorMessages(result).join("\n"));

const PUBLIC_POLL_OPTION_RESULT = `
      code
      errors {
        name
        errors
      }
      data {
        pollQuestionId
        optionId
      }`;

const PUBLIC_POLL_ADD_OPTION_MUTATION = gql`
  mutation PublicPollAddOption($params: PublicPollAddOptionInput) {
    publicPollAddOption(params: $params) {${PUBLIC_POLL_OPTION_RESULT}
    }
  }
`;

const PUBLIC_POLL_EDIT_OPTION_MUTATION = gql`
  mutation PublicPollEditOption($params: PublicPollEditOptionInput) {
    publicPollEditOption(params: $params) {${PUBLIC_POLL_OPTION_RESULT}
    }
  }
`;

const PUBLIC_TRAVEL_MUTATION = gql`
  mutation PublicTravel($params: PublicTravelInput) {
    publicTravel(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        savedCount
        party {
          ref
          firstName
          lastName
        }
      }
    }
  }
`;

const PUBLIC_TRAVEL_HUBS_QUERY = gql`
  query PublicTravelHubs($url: String!, $query: String!, $mode: String) {
    publicTravelHubs(url: $url, query: $query, mode: $mode) {
      code
      errors {
        name
        errors
      }
      data {
        id
        name
        code
        timezone
        kind
        locality
        regionCode
        countryCode
      }
    }
  }
`;

const PUBLIC_BUDGET_MUTATION = gql`
  mutation PublicBudget($params: PublicBudgetInput) {
    publicBudget(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        pledgedCount
        pledgedTotal
        paidCount
        paidTotal
        party {
          ref
          firstName
          lastName
        }
      }
    }
  }
`;

const PUBLIC_UPVOTE_MUTATION = gql`
  mutation PublicUpvote($params: PublicUpvoteInput) {
    publicUpvote(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        targetKind
        targetId
        upvoted
        count
      }
    }
  }
`;

const PUBLIC_UPVOTES_MINE_QUERY = gql`
  query PublicUpvotesMine($url: String!, $visitorId: String, $eventTimeSlotId: ID) {
    publicUpvotesMine(url: $url, visitorId: $visitorId, eventTimeSlotId: $eventTimeSlotId) {
      code
      errors {
        name
        errors
      }
      data {
        found
        upvotes {
          targetKind
          targetId
        }
      }
    }
  }
`;

const PUBLIC_COUPONS_MINE_QUERY = gql`
  query PublicCouponsMine($params: PublicRsvpMineInput) {
    publicCouponsMine(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        event {
          id
          title
          slug
        }
        found
        coupons {
          id
          code
          couponTypeId
          name
          description
          color
          icon
          couponValue
          items {
            name
            quantity
          }
          couponStatus
          redeemedCount
          maxRedemptions
          issuedToLabel
          guestName
          eventTimeSlotId
          dateLabel
          dateStartsAt
          redeemAt
        }
      }
    }
  }
`;

export class PublicRsvpService {
  /**
   * Inline signup from the signup block (a KNOWN visitor). Replaces the party's signups
   * for one item with the given lines (across party guests, multiple lines allowed).
   */
  static async signup(input: PublicSignupInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_SIGNUP_MUTATION, { params: input }, options);
  }

  /** #246 — a known guest removes an item their party added, while no other guest is signed up for it. */
  static async removeSignupItem(input: PublicSignupRemoveItemInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_SIGNUP_REMOVE_ITEM_MUTATION, { params: input }, options);
  }

  /** #246 — a known guest adds their own item to the sign-up list, and signs up for it if they give a quantity. */
  static async addSignupItem(input: PublicSignupAddItemInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_SIGNUP_ADD_ITEM_MUTATION, { params: input }, options);
  }

  /**
   * Answer ONE poll/quiz question for party members inline (a KNOWN visitor). Each line is
   * a guest's answer (option ids OR free text) and replaces their prior answer.
   */
  static async pollAnswer(input: PublicPollAnswerInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_POLL_ANSWER_MUTATION, { params: input }, options);
  }

  /**
   * Aggregate poll tallies for an event's questions (for the results view). Pass the
   * visitorId to enable the post-answer quiz correctness reveal. Always a fresh read.
   */
  static async pollResults(
    args: { url: string; pollQuestionIds?: string[] | null; visitorId?: string | null; eventTimeSlotId?: string | null },
    options: Record<string, any> = {},
  ): Promise<OpResult> {
    const variables = {
      params: {
        url: args.url,
        pollQuestionIds: args.pollQuestionIds ?? null,
        visitorId: args.visitorId ?? null,
        eventTimeSlotId: args.eventTimeSlotId ?? null,
      },
    };
    const result = await ApiService.executeQuery(PUBLIC_POLL_RESULTS_QUERY, variables, { fetchPolicy: 'network-only', ...options });

    // #147 — a gateway not yet recomposed costs the in-block results rules, never the poll.
    return refusedPollBlockFields(result)
      ? ApiService.executeQuery(PUBLIC_POLL_RESULTS_QUERY_BASE, variables, { fetchPolicy: 'network-only', ...options })
      : result;
  }

  /** #147 — a guest adds their own option to a poll question; it becomes their answer. */
  static async pollAddOption(input: PublicPollOptionInput, options: Record<string, any> = {}): Promise<OpResult> {
    const { optionId: _ignored, ...params } = input;

    return ApiService.executeMutation(PUBLIC_POLL_ADD_OPTION_MUTATION, { params }, options);
  }

  /** #147 — a guest renames the option they added, while nobody else has chosen it. */
  static async pollEditOption(input: PublicPollOptionInput & { optionId: string }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_POLL_EDIT_OPTION_MUTATION, { params: input }, options);
  }

  /**
   * Save a party's self-reported travel as REAL travel segments (a KNOWN visitor). Replaces the
   * party's prior guest-reported segments; host-planned travel is untouched.
   */
  static async travelShare(input: PublicTravelInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TRAVEL_MUTATION, { params: input }, options);
  }

  /**
   * Search the travel-hub catalog (airports/stations) for the from/to picker. Public reference
   * data — no visitor required. `mode` narrows the hub kind (flight→airport, train→station, …).
   */
  static async travelHubs(
    args: { url: string; query: string; mode?: string | null },
    options: Record<string, any> = {},
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_TRAVEL_HUBS_QUERY,
      { url: args.url, query: args.query, mode: args.mode ?? null },
      { fetchPolicy: 'network-only', ...options },
    );
  }

  /**
   * Pledge amounts toward the event budget inline (a KNOWN visitor). Each contribution is a
   * pledge toward an existing item or a new one; the set replaces the guest's prior pledges.
   */
  static async budgetContribute(input: PublicBudgetInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_BUDGET_MUTATION, { params: input }, options);
  }

  /**
   * Toggle (or set) the party's upvote on a signup item / POI / accommodation (a KNOWN
   * visitor). Returns an OpResult whose data[0] is a PublicUpvoteResultData (new state + count).
   */
  static async upvote(input: PublicUpvoteInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_UPVOTE_MUTATION, { params: input }, options);
  }

  /**
   * Load the upvotes this visitor's party has cast for an event, so each pill renders in its
   * toggled state on load. Always a fresh read. Returns found:false when the browser has none.
   */
  static async upvotesMine(
    args: { url: string; visitorId?: string | null; eventTimeSlotId?: string | null },
    options: Record<string, any> = {},
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_UPVOTES_MINE_QUERY,
      { url: args.url, visitorId: args.visitorId ?? null, eventTimeSlotId: args.eventTimeSlotId ?? null },
      { fetchPolicy: 'network-only', ...options },
    );
  }

  /**
   * Load the RSVP this visitor previously made for an event (remember-me / edit).
   * Always a fresh network read. Returns found:false when the browser has none.
   */
  static async getMine(
    args: { url: string; visitorId?: string | null; eventTimeSlotId?: string | null },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return executeQueryImageSafe(
      PUBLIC_RSVP_MINE_QUERY,
      { params: { url: args.url, visitorId: args.visitorId ?? null, eventTimeSlotId: args.eventTimeSlotId ?? null } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /**
   * Load THIS visitor's own coupons for an event (the "show a venue rep" read). Keyed only on the
   * visitor cookie, so it never returns another guest's coupons. Always a fresh network read (redemption
   * state must be current). Returns found:false when the browser has no RSVP here.
   */
  static async couponsMine(
    args: { url: string; visitorId?: string | null; eventTimeSlotId?: string | null },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_COUPONS_MINE_QUERY,
      { params: { url: args.url, visitorId: args.visitorId ?? null, eventTimeSlotId: args.eventTimeSlotId ?? null } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /**
   * Load an invited guest's prefill from their personal invite token (`?inv=`).
   * Same PublicRsvpMine shape as getMine, privacy-gated by `visitorId`:
   *  · not yet responded → host prefill (`invited:true`).
   *  · responded, SAME device (visitorId matches the party) → full data (`responded:true`, `invited:false`).
   *  · responded, a DIFFERENT device → data withheld (`requiresCode:true`) — enter a code to view/change.
   * Pass the browser's `em_visitor` id so the server can recognise the same device. Always a fresh read.
   */
  static async getInvite(
    inviteToken: string,
    visitorId?: string | null,
    options: Record<string, any> = {},
  ): Promise<OpResult> {
    return executeQueryImageSafe(
      PUBLIC_RSVP_INVITE_QUERY,
      { params: { inviteToken, visitorId: visitorId ?? null } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /**
   * Load the RSVP form (accepting-state + custom questions) for an event. Always a
   * fresh read — spots-left and open/closed state must not be cached.
   */
  static async getForm(
    // 2026-10-04 — `visitorId` / `inviteToken`: on an invite-only event the form is its guests' only. Each is sent only
    // when there is one (a gateway that predates them refuses the fields).
    args: { url: string; eventTimeSlotId?: string | null; visitorId?: string | null; inviteToken?: string | null },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    const visitorId = (args.visitorId ?? "").trim();
    const inviteToken = (args.inviteToken ?? "").trim();
    const base = { url: args.url, eventTimeSlotId: args.eventTimeSlotId ?? null };
    const opts = { fetchPolicy: "network-only", ...options };
    const result = await executeQueryImageSafe(
      PUBLIC_RSVP_FORM_QUERY,
      { params: { ...base, ...(visitorId ? { visitorId } : {}), ...(inviteToken ? { inviteToken } : {}) } },
      opts
    );

    // An API / gateway deployed after the site does not know the new fields — read the form as before.
    return (visitorId || inviteToken) && refusedNewInputs(result, ["visitorId", "inviteToken"])
      ? executeQueryImageSafe(PUBLIC_RSVP_FORM_QUERY, { params: base }, opts)
      : result;
  }

  /** Submit an RSVP. Returns an OpResult whose data[0] is a PublicRsvpOutcome. */
  static async submit(input: PublicRsvpInput, options: Record<string, any> = {}): Promise<OpResult> {
    return executeMutationImageSafe(PUBLIC_RSVP_MUTATION, { params: input }, options);
  }

  /**
   * Load a self-sign waiver by its acceptance token (the tokenized "sign later" link), for the
   * public signing page. Always a fresh read. data[0] is a PublicPolicyToken.
   */
  static async policyToken(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    const result = await ApiService.executeQuery(PUBLIC_POLICY_TOKEN_QUERY, { token }, { fetchPolicy: "network-only", ...options });

    if (refusedUnknownPolicyBrand(result)) {
      return ApiService.executeQuery(PUBLIC_POLICY_TOKEN_QUERY_BASE, { token }, { fetchPolicy: "network-only", ...options });
    }

    return result;
  }

  /** Sign a self-sign waiver via its token — records the signature + accepts it. */
  static async policySign(input: PublicPolicySignInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_POLICY_SIGN_MUTATION, { params: input }, options);
  }

  // ── Waitlist claim (ROADMAP #36) ──

  /**
   * Load a waitlist invitation by its signed token, for the public claim page. Always a fresh read
   * — the offer can lapse between the email arriving and the link being opened. data[0] is a
   * PublicWaitlistOffer whose `found` / `claimed` / `expired` flags are STATES the page renders,
   * not errors.
   */
  static async waitlistOffer(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(PUBLIC_WAITLIST_OFFER_QUERY, { token }, { fetchPolicy: "network-only", ...options });
  }

  /** Accept the held place. Idempotent — a double submit reports `alreadyIn`, not an error. */
  static async waitlistClaim(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_WAITLIST_CLAIM_MUTATION, { token }, options);
  }

  /** Hand the place back so it goes to the next person straight away. */
  static async waitlistDecline(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_WAITLIST_DECLINE_MUTATION, { token }, options);
  }

  // ── Cross-device RSVP restore ──

  /**
   * Ask us to send a 6-digit access code to the email/phone the guest RSVP'd with.
   * ALWAYS resolves neutrally (data[0].sent) — it never reveals whether the contact
   * matched a guest, so the UI just says "if that matches, we sent a code".
   */
  static async requestCode(url: string, contact: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_RSVP_REQUEST_CODE_MUTATION, { url, contact }, options);
  }

  /** From the ORIGINAL browser (holds the visitor cookie): mint a 6-digit code to type on a new device (data[0].code). */
  static async transferCode(url: string, visitorId: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_RSVP_TRANSFER_CODE_MUTATION, { url, visitorId }, options);
  }

  /**
   * #69 — a guest this browser answers for removes every photo of themselves from the event site (`hidden: true`), or
   * shows it again. data[0].photoHidden is the state now.
   */
  static async guestPhoto(input: PublicRsvpGuestPhotoInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_RSVP_GUEST_PHOTO_MUTATION, { params: input }, options);
  }

  /**
   * A2e #91 — upload the picture that answers a picture question, on any public form (RSVP, tickets, sponsorship,
   * volunteering). `dataBase64` is the picture already cropped and shrunk in the browser (JPEG or PNG, at most 4 MB).
   * data[0] is the answer's `valueImage` (file + token) plus the stored file's URL. A refusal names the field
   * `dataBase64` with the sentence to show under the question.
   */
  static async uploadAnswerImage(
    input: { url: string; questionId: string; visitorId: string; dataBase64: string },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_ANSWER_IMAGE_UPLOAD_MUTATION, { params: input }, options);
  }

  /** From the ORIGINAL browser: fetch/ensure the party's durable friendly recovery code to display (data[0].code). */
  static async myCode(url: string, visitorId: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_RSVP_MY_CODE_MUTATION, { url, visitorId }, options);
  }

  /**
   * #62 P6c — send a 6-digit code to the INVITED guest's own contact on file (never one typed here). data[0].sentTo is
   * where it went, masked. Redeem it with `redeemCode`; the invite read is then trusted and carries the real details.
   */
  static async requestInviteCode(url: string, inviteToken: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_RSVP_INVITE_REQUEST_CODE_MUTATION, { params: { url, inviteToken } }, options);
  }

  /**
   * Redeem a 6-digit code (OTP / transfer) OR the durable friendly recovery code on a NEW
   * browser. On success data[0].inviteToken is the party's invite token — navigate to
   * `?inv=<inviteToken>` to prefill + bind this device. data[0].visitorId is the party's
   * existing visitor id (may be null if they never RSVP'd) — the VisitorSessionProvider
   * ADOPTs it so all their per-visitor data (upvotes/signups/…) lights up on the new browser.
   */
  static async redeemCode(url: string, code: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_RSVP_REDEEM_CODE_MUTATION, { url, code }, options);
  }
}
